import React, {FunctionComponent, useMemo} from "react";
import {Grid} from "@mui/material";
import { EmptyState} from "@atiautomacao/ati-ui-library";
import {useQueryClient} from "react-query";
import {NavigationEquipment, NavigationPowerStation, NavigationSkid} from "../../../../Shared/Types/Navigation";
import {FindBessPowerRangeConfigReactQuery} from "./BatteryReactQuery";
import BatteryCard from "./BatteryCard";
import BessTotalizers from "./BessTotalizers";
import {useAppSelector} from "../../../../Config/Hooks";
import {RootState} from "../../../../Config/Store";
import useInterval from "../../../../Shared/Hooks/useInterval";

// Convenção de tipo de equipamento "BESS - Battery Storage" (mesmo id usado em DashboardHeaderMenuMediumLargeScreens)
const BESS_TYPE_ID = 24;

interface BessEntry {
    equipment: NavigationEquipment;
    skid?: NavigationSkid;
}

const BatteriesPage: FunctionComponent<{ powerPlant: NavigationPowerStation }> = ({powerPlant}) => {
    const powerRangeQuery = FindBessPowerRangeConfigReactQuery();
    const minPower = powerRangeQuery.data?.data?.minPower ?? null;
    const maxPower = powerRangeQuery.data?.data?.maxPower ?? null;

    const queryClient = useQueryClient();
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);

    useInterval(() => {
        queryClient.refetchQueries({ queryKey: ["bessTotalizers"], active: true });        // totalizadores
        queryClient.refetchQueries({ queryKey: ["bessTeleObjectData"], active: true });     // card principal + gauges SOC por BMS
        queryClient.refetchQueries({ queryKey: ["activeTeleObjectAlarms"], active: true }); // coloração de alarme dos gauges
    }, userConfig.alarmUpdateInterval);

    const bessEntries: BessEntry[] = useMemo(() => {
        if (!powerPlant) return [];
        const entries: BessEntry[] = [];

        (powerPlant.equipmentList || []).forEach((equipment) => {
            if (equipment.equipmentType?.id === BESS_TYPE_ID) {
                entries.push({equipment});
            }
        });

        ((powerPlant as any).skidList || []).forEach((skid: NavigationSkid) => {
            (skid.equipmentList || []).forEach((equipment) => {
                if (equipment.equipmentType?.id === BESS_TYPE_ID) {
                    entries.push({equipment, skid});
                }
            });
        });

        return entries;
    }, [powerPlant]);

    return (
        <Grid container spacing={2}>

            {bessEntries.length > 0 && (
                <Grid item xs={12}>
                    <BessTotalizers powerStationId={powerPlant?.id}/>
                </Grid>
            )}

            {bessEntries.length === 0 ? (
                <Grid item xs={12}>
                    <EmptyState
                        title="Nenhum equipamento BESS encontrado"
                        description="Esta usina não possui equipamentos do tipo BESS - Battery Storage cadastrados."
                    />
                </Grid>
            ) : (
                bessEntries.map(({equipment, skid}) => (
                    <Grid item xs={12} md={6} key={equipment.id} >
                        <BatteryCard
                            powerPlant={powerPlant}
                            equipment={equipment}
                            skid={skid}
                            minPower={minPower}
                            maxPower={maxPower}
                        />
                    </Grid>
                ))
            )}
        </Grid>
    );
};

export default BatteriesPage;
